import { Component } from '@theme/component';

/**
 * A contact-topic form inside a theme-drawer (snippets/contact-drawer.liquid).
 *
 * Two behaviors, selected by `data-type`:
 *
 * - 'orders' | 'stores' | 'business': builds the multipart body the BasicNet
 *   contacts app proxy expects — a `payload` JSON part plus one `attachments`
 *   part per selected file — and POSTs it to /apps/contacts/drawer. The
 *   payload keys mirror the legacy theme verbatim (including the redundant
 *   stores* keys) because the app backend is not inspectable; do not rename
 *   them without app-team sign-off.
 * - 'tracking': never POSTs to the app proxy. It calls AfterShip's tracking
 *   endpoints directly from the browser (CORS is open to the storefront),
 *   then replaces the form with a minimal shipment summary. See #fetchTracking
 *   for why this is a browser call and what it costs.
 *
 * Validation is native (`required`, `type=email`, `pattern`) surfaced through
 * the theme's `aria-invalid` hook: base.css paints the red/pink error state,
 * this component only toggles the attribute and the form-level error line.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form - The form element.
 * @property {HTMLButtonElement} submitButton - The submit button.
 * @property {HTMLElement} [formError] - Form-level error line.
 * @property {HTMLInputElement} [fileInput] - Hidden attachments input.
 * @property {HTMLElement} [fileNames] - Selected-file-names preview.
 * @property {HTMLSelectElement} [requestType] - Request-subject select.
 * @property {HTMLSelectElement} [pointOfSale] - Where-did-you-buy select.
 * @property {HTMLTemplateElement} [successTemplate] - Success panel markup.
 *
 * @extends {Component<Refs>}
 */
class ContactFormComponent extends Component {
  requiredRefs = ['form', 'submitButton'];

  /** Matches legacy order numbers like `#SE-12345`; the `#` is optional. */
  static #orderNumberPattern = /^[#]?[A-Z]{2}-\d{5,}$/i;

  /**
   * AfterShip serves these procedures from one shared host for every merchant —
   * `username` is literal, not a placeholder. Account identity travels in the
   * organization id.
   */
  static #aftershipTrpc = 'https://username.aftership.com/trpc/';

  /**
   * AfterShip's errorCode for an order it knows about but will not expose
   * through the anonymous orders endpoint yet — i.e. placed but unfulfilled.
   */
  static #orderNotFulfilledYet = 40442;

  /**
   * Cap on each AfterShip call. These are undocumented third-party procedures:
   * if one hangs, the timeout routes the lookup to the hosted-page fallback
   * instead of leaving the submit button stuck on its busy label.
   */
  static #lookupTimeoutMs = 10000;

  /** Maps a canonical AfterShip status (`OutForDelivery`) to a locale key (`out_for_delivery`). */
  static #statusKey = (status) =>
    String(status ?? '')
      .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
      .toLowerCase();

  /** The Liquid-rendered validation copy, restored after a submit error overwrote it. */
  #validationMessage = '';

  connectedCallback() {
    super.connectedCallback();

    this.#validationMessage = this.refs.formError?.textContent ?? '';
    // Suppress native bubbles only when this component is live to drive the
    // aria-invalid error styling itself; without JS the browser still blocks
    // invalid submits natively.
    this.refs.form.noValidate = true;

    if (this.dataset.type === 'tracking') {
      // Surface missing configuration at load rather than as what looks like
      // an AfterShip outage on the first lookup — the merchant previewing the
      // page sees this in the console before a shopper ever hits it.
      if (!this.dataset.aftershipOrganizationId) {
        console.warn(
          'Contact tracking drawer: the AfterShip organization id is empty, so order lookups will fail. Set it on the tracking topic block in the theme editor.'
        );
      }
      this.#prefillTrackingFromQuery();
    }
  }

  /**
   * Prefills the lookup from `order` / `email` query params, so a support
   * reply or a transactional email can deep-link a customer straight into
   * their own lookup: /pages/contact?order=%23SE-12345&email=a%40b.com#tracking
   *
   * Values are only ever written into empty fields — anything already typed
   * wins — and garbage in the URL is no worse than garbage typed by hand: it
   * meets the same validation on submit.
   */
  #prefillTrackingFromQuery() {
    const params = new URLSearchParams(window.location.search);
    const values = { orderNumber: params.get('order'), email: params.get('email') };

    for (const [name, value] of Object.entries(values)) {
      const field = this.refs.form.elements.namedItem(name);
      if (field instanceof HTMLInputElement && value && !field.value) field.value = value;
    }
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();

    if (!this.#validate()) return;

    // Tracking never posts to the contacts backend; it queries AfterShip and
    // renders the result in place. Guarding on type here (rather than letting
    // it fall through) keeps a tracking form from ever filing a support ticket.
    if (this.dataset.type === 'tracking') {
      this.#openTracking();
    } else {
      this.#submitRequest();
    }
  }

  /**
   * Clears the error state of fields that became valid, and drops the
   * form-level error line once every field is valid again.
   *
   * Sweeps the whole form instead of reading event.target: the on:input
   * attribute lives on the <form>, and the component framework rewrites
   * event.target to the attribute's element, so the edited control is not
   * observable here.
   */
  handleFieldInput() {
    const { form, formError } = this.refs;

    for (const element of form.elements) {
      const field = /** @type {HTMLInputElement} */ (element);
      if (field.getAttribute?.('aria-invalid') === 'true' && field.validity?.valid) {
        this.#setFieldError(field, false);
      }
    }

    if (formError && form.checkValidity()) {
      formError.hidden = true;
    }
  }

  /**
   * Mirrors the chosen file names next to the styled upload button.
   */
  handleFileChange() {
    const { fileInput, fileNames } = this.refs;
    if (!fileInput || !fileNames) return;

    fileNames.textContent = Array.from(fileInput.files ?? [])
      .map((file) => file.name)
      .join(', ');
  }

  /**
   * Toggles `aria-invalid` across the form and surfaces the form-level error.
   *
   * @returns {boolean} Whether the form is valid.
   */
  #validate() {
    const { form, formError } = this.refs;
    /** @type {HTMLElement | null} */
    let firstInvalid = null;

    for (const element of form.elements) {
      if (!('validity' in element) || element.disabled) continue;
      const field = /** @type {HTMLInputElement} */ (element);

      if (field.willValidate && !field.validity.valid) {
        this.#setFieldError(field, true);
        firstInvalid ??= field;
      } else {
        this.#setFieldError(field, false);
      }
    }

    if (firstInvalid) {
      this.#showFormError(this.#validationMessage);
      firstInvalid.focus();
    } else if (formError) {
      formError.hidden = true;
    }

    return !firstInvalid;
  }

  /**
   * @param {string} message - Copy for the form-level error line. The element
   *   is shared between validation and submit errors, so each path passes its
   *   own text instead of relying on whatever the last failure left behind.
   * @param {{url?: string, label?: string}} [link] - Optional escape hatch
   *   appended after the message — the tracking error copy promises the hosted
   *   page, so that promise has to be an actual link, not just words.
   */
  #showFormError(message, link) {
    const { formError } = this.refs;
    if (!formError) return;

    // Assigning textContent also drops the link a previous error appended.
    formError.textContent = message;
    if (link?.url && link.label) {
      const anchor = document.createElement('a');
      anchor.href = link.url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.textContent = link.label;
      formError.append(' ', anchor);
    }
    formError.hidden = false;
  }

  /**
   * @param {string} name
   * @returns {string} The trimmed value of the named form field.
   */
  #value(name) {
    const field = this.refs.form.elements.namedItem(name);
    return field instanceof HTMLElement && 'value' in field ? String(field.value).trim() : '';
  }

  /**
   * @param {HTMLSelectElement} [select]
   * @returns {string} The visible label of the selected option — the backend
   *   receives human-readable request types (legacy mirrored these through
   *   hidden inputs; reading selectedOptions at submit time replaces that).
   */
  #selectedLabel(select) {
    const option = select?.selectedOptions[0];
    if (!option || option.disabled) return '';
    return option.textContent.trim();
  }

  /**
   * @returns {object} The `payload` JSON part, keyed exactly like the legacy theme.
   */
  #buildPayload() {
    const { requestType, pointOfSale } = this.refs;
    const store = /** @type {HTMLElement | null} */ (this.closest('[data-store]'))?.dataset.store ?? '';
    const base = {
      type: this.dataset.type,
      store,
      firstName: this.#value('firstName'),
      lastName: this.#value('lastName'),
      email: this.#value('email'),
      subject: this.dataset.subject ?? '',
      message: this.#value('message'),
    };

    switch (this.dataset.type) {
      case 'orders':
        return {
          ...base,
          orderNumber: this.#value('orderNumber'),
          requestTypeLabel: this.#selectedLabel(requestType),
        };
      case 'business':
        return {
          ...base,
          company: this.#value('company'),
          requestTypeLabel: this.#selectedLabel(requestType),
          businessRequestTypeValue: requestType?.value ?? '',
        };
      case 'stores':
        return {
          ...base,
          pointOfSale: this.#selectedLabel(pointOfSale),
          purchaseDate: this.#value('purchaseDate'),
          storesHiddenInput: this.#selectedLabel(requestType),
          storesRequestPointOfSaleInput: this.#selectedLabel(pointOfSale),
          storesRequestPointOfSaleInputValue: pointOfSale?.value ?? '',
        };
      default:
        return base;
    }
  }

  async #submitRequest() {
    const { form, fileInput } = this.refs;

    const body = new FormData();
    body.append('payload', JSON.stringify(this.#buildPayload()));
    for (const file of fileInput?.files ?? []) {
      body.append('attachments', file, file.name);
    }

    this.#setBusy(true);
    try {
      const response = await fetch(form.action, { method: 'POST', body });
      if (!response.ok) throw new Error(`Contact request failed with ${response.status}`);
      this.#showSuccess();
    } catch (error) {
      console.error(error);
      this.#showFormError(this.dataset.submitError ?? '');
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * Hides the form and shows the success panel — only after the response
   * arrived OK (the legacy theme painted success before parsing). The form
   * stays in the DOM: removing it would strip a requiredRef and make the
   * Component base's MutationObserver throw on the mutation.
   */
  #showSuccess() {
    const { form, successTemplate } = this.refs;
    if (!successTemplate) return;

    form.hidden = true;
    form.after(successTemplate.content.cloneNode(true));
  }

  async #openTracking() {
    const email = this.#value('email');
    let orderNumber = this.#value('orderNumber');

    if (!ContactFormComponent.#orderNumberPattern.test(orderNumber)) {
      this.#markInvalid('orderNumber');
      return;
    }
    if (!orderNumber.startsWith('#')) orderNumber = `#${orderNumber}`;
    // Shopify order names are canonically uppercase and AfterShip may match
    // case-sensitively — a lowercase-typed number must not read as a miss.
    orderNumber = orderNumber.toUpperCase();

    // `||` and not `??`: a locale override can empty a label to '', and an
    // empty error line is worse than the generic submit error.
    const { labels } = this.#trackingI18n();
    const fallbackError = this.dataset.submitError ?? '';
    this.#setBusy(true);
    try {
      const { payload, token, errorCode } = await this.#fetchTracking(orderNumber, email);
      const summary = ContactFormComponent.#normalizeTracking(payload, token, this.dataset.trackingPageUrl);

      if (!summary) {
        // 40442 is AfterShip's "the order exists but has nothing trackable
        // yet": the anonymous orders endpoint withholds unfulfilled orders
        // (a wrong number/email pair comes back as 40412 instead). Show a
        // processing state, not a miss.
        if (errorCode === ContactFormComponent.#orderNotFulfilledYet) {
          this.#renderTrackingSummary(ContactFormComponent.#processingSummary(orderNumber, labels));
          return;
        }
        this.#showFormError(labels.not_found || fallbackError);
        return;
      }
      if (!summary.trackingNumber) {
        // The order is readable but nothing has been handed to a courier —
        // same processing presentation, with the order name AfterShip returned.
        this.#renderTrackingSummary(ContactFormComponent.#processingSummary(summary.orderName, labels));
        return;
      }
      this.#renderTrackingSummary(summary);
    } catch (error) {
      // A lookup failure must not be a dead end: the hosted tracking page can
      // still do the job, so the error carries an actual link to it. The
      // lookup_failed copy promises that page, which is only honest when
      // there is one to link — without a URL, the generic submit error.
      console.error(error);
      const hostedPage = ContactFormComponent.#hostedPageUrl(this.dataset.trackingPageUrl);
      this.#showFormError(hostedPage ? labels.lookup_failed || fallbackError : fallbackError, {
        url: hostedPage,
        label: labels.view_full_tracking ?? '',
      });
    } finally {
      this.#setBusy(false);
    }
  }

  /**
   * @param {string} orderName - Order name to show, `#`-prefixed.
   * @param {Record<string, string>} labels - Tracking labels from the i18n island.
   * @returns {object} Summary for an order that exists but has nothing trackable
   *   yet. The status is a synthetic value with its own locale entry (AfterShip
   *   never returns it), and there is deliberately no hosted-page link: the
   *   hosted page has nothing to add for an order without shipments.
   */
  static #processingSummary(orderName, labels) {
    return {
      orderName,
      status: 'ToBeProcessed',
      courierName: '',
      trackingNumber: '',
      deliveredAt: '',
      note: labels.no_shipment ?? '',
      fullTrackingUrl: '',
    };
  }

  /**
   * Looks the order up against AfterShip in two steps: mint a customer token
   * from order number + email, then read the order with it.
   *
   * These procedures are not part of AfterShip's documented API — they are not
   * even in their SDK bundle, which loads the calling code at runtime. That
   * makes this a deliberate trade: no backend to maintain, but the shape can
   * change without notice, so every failure path falls back to the hosted page.
   *
   * @param {string} orderNumber - Normalised order name, `#`-prefixed.
   * @param {string} email - Email the order was placed with.
   * @returns {Promise<{payload: object, token: string, errorCode?: number}>}
   */
  async #fetchTracking(orderNumber, email) {
    const organizationId = this.dataset.aftershipOrganizationId;
    if (!organizationId) {
      throw new Error('AfterShip tracking is not configured: set the organization id on the tracking topic block');
    }
    const base = ContactFormComponent.#aftershipTrpc;

    const minted = await fetch(`${base}orders.createOrderURLWithCustomerInfo?batch=1`, {
      method: 'POST',
      signal: AbortSignal.timeout(ContactFormComponent.#lookupTimeoutMs),
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        0: {
          organization_id: organizationId,
          customer: { email },
          order: { store_order_number: orderNumber },
          encrypt: { enabled: true },
        },
      }),
    });
    if (!minted.ok) throw new Error(`AfterShip order lookup failed: ${minted.status}`);

    const mintedJson = await minted.json();
    const orderUrl = (Array.isArray(mintedJson) ? mintedJson[0] : mintedJson)?.result?.data?.order_url ?? '';
    const token = decodeURIComponent((orderUrl.split('c_token=')[1] ?? '').split('&')[0]);

    // AfterShip mints a token even for orders that do not exist — a genuine
    // miss surfaces as errorCode 40412 from the read below. A missing token
    // therefore means the mint response changed shape: exactly the contract
    // break this integration is hedged against, so it belongs in the catch,
    // which links out to the hosted page.
    if (!token) throw new Error('AfterShip mint response carried no customer token');

    const input = {
      organization_id: organizationId,
      url_id: token,
      // Localises the courier's checkpoint text; the canonical status stays
      // English, which is why the status headline is translated in Liquid.
      // Bare code, like the hosted-page link: region-tagged values (pt-PT)
      // are silently ignored and come back English.
      translate_to: ContactFormComponent.#langCode() || 'en',
    };
    const read = await fetch(`${base}orders.getOrdersWithOrderUrlId?input=${encodeURIComponent(JSON.stringify(input))}`, {
      signal: AbortSignal.timeout(ContactFormComponent.#lookupTimeoutMs),
    });
    if (!read.ok) throw new Error(`AfterShip order read failed: ${read.status}`);

    const readJson = await read.json();
    const data = readJson?.result?.data;
    return { payload: data ?? { orders: [] }, token, errorCode: data?.errorCode };
  }

  /**
   * @returns {string} URL-safe bare language code (`it`, not `it-IT`) — the
   *   only form the hosted page's `lang` param accepts — or ''.
   */
  static #langCode() {
    return encodeURIComponent((document.documentElement.lang || '').split('-')[0]);
  }

  /**
   * @param {string} [trackingPageUrl] - Hosted tracking page base URL.
   * @param {string} [token] - Customer token minted for this order.
   * @returns {string} The hosted page deep-linked to the order, or ''.
   */
  static #hostedOrderUrl(trackingPageUrl, token) {
    if (!trackingPageUrl || !token) return '';
    // The hosted page ignores the shopper's storefront language unless told.
    const lang = ContactFormComponent.#langCode();
    const langParam = lang ? `&lang=${lang}` : '';
    return `${trackingPageUrl.replace(/\/$/, '')}/order-status?c_token=${encodeURIComponent(token)}${langParam}`;
  }

  /**
   * @param {string} [trackingPageUrl] - Hosted tracking page base URL.
   * @returns {string} The hosted page's landing URL (no order attached), used
   *   when a lookup failed and there is no token to deep-link with, or ''.
   */
  static #hostedPageUrl(trackingPageUrl) {
    if (!trackingPageUrl) return '';
    const lang = ContactFormComponent.#langCode();
    return trackingPageUrl.replace(/\/$/, '') + (lang ? `?lang=${lang}` : '');
  }

  /**
   * Reduces the AfterShip payload to what the drawer shows. Everything else it
   * carries — prices, item titles, the recipient signature, the full checkpoint
   * history — is dropped here on purpose.
   *
   * @param {object} payload - AfterShip `{ orders: [...] }` payload.
   * @param {string} token - Customer token, used to deep-link the hosted page.
   * @param {string} [trackingPageUrl] - Hosted tracking page base URL.
   * @returns {object | null} Summary, or null when no order matched.
   */
  static #normalizeTracking(payload, token, trackingPageUrl) {
    const order = payload?.orders?.[0];
    if (!order) return null;

    // An order can be split across fulfilments; show the first one carrying a
    // tracking number rather than inventing a combined status.
    const tracking = (order.fulfillments ?? [])
      .flatMap((fulfillment) => fulfillment.trackings ?? [])
      .find((candidate) => candidate?.tracking_number);

    // Prefer the branded page deep-linked to this order — it is multi-language
    // and shows the full timeline. The carrier's own page is the fallback.
    const fullTrackingUrl =
      ContactFormComponent.#hostedOrderUrl(trackingPageUrl, token) || (tracking?.carrier_tracking_link?.url ?? '');

    return {
      orderName: order.order_name ?? order.order_number ?? '',
      status: tracking?.status ?? '',
      courierName: tracking?.courier?.name ?? '',
      trackingNumber: tracking?.tracking_number ?? '',
      // Only a delivered shipment gets its date shown: some couriers populate
      // shipment_delivery_date with an estimate while still in transit, and an
      // "In transit" headline over a "Delivered on" row reads as a contradiction.
      deliveredAt: (tracking?.status === 'Delivered' && tracking?.shipment_delivery_date) || '',
      fullTrackingUrl,
    };
  }

  /**
   * @returns {{statuses: Record<string, string>, labels: Record<string, string>}}
   */
  #trackingI18n() {
    const island = this.querySelector('[data-tracking-i18n]');
    try {
      const parsed = JSON.parse(island?.textContent ?? '{}');
      return { statuses: parsed.statuses ?? {}, labels: parsed.labels ?? {} };
    } catch {
      return { statuses: {}, labels: {} };
    }
  }

  /**
   * Swaps the form for the result. Built with DOM calls rather than an HTML
   * string so courier and order values can never be parsed as markup.
   *
   * @param {{orderName: string, status: string, courierName: string,
   *          trackingNumber: string, deliveredAt: string,
   *          fullTrackingUrl: string, note?: string}} summary
   */
  #renderTrackingSummary(summary) {
    const { form } = this.refs;
    const { statuses, labels } = this.#trackingI18n();

    // Looking up a second order would otherwise stack panels below the first.
    this.querySelector('.contact-drawer__tracking')?.remove();

    const panel = document.createElement('div');
    panel.className = 'contact-drawer__tracking';
    // Hiding the form drops focus to <body>, i.e. outside the modal dialog, so
    // the result has to take focus itself: that both announces it and gives
    // keyboard users somewhere sensible to continue tabbing from.
    panel.tabIndex = -1;
    panel.setAttribute('role', 'status');

    const order = document.createElement('p');
    order.className = 'contact-drawer__tracking-order';
    order.textContent = summary.orderName;
    panel.append(order);

    const statusKey = ContactFormComponent.#statusKey(summary.status);
    const status = document.createElement('p');
    status.className = 'contact-drawer__tracking-status';
    status.textContent = statuses[statusKey] ?? summary.status;
    panel.append(status);

    if (summary.note) {
      const note = document.createElement('p');
      note.className = 'contact-drawer__tracking-note';
      note.textContent = summary.note;
      panel.append(note);
    }

    const meta = document.createElement('dl');
    meta.className = 'contact-drawer__tracking-meta';
    const rows = [
      [labels.courier, summary.courierName],
      [labels.tracking_number, summary.trackingNumber],
      [labels.delivered_on, this.#formatDate(summary.deliveredAt)],
    ];
    for (const [term, value] of rows) {
      if (!term || !value) continue;
      const dt = document.createElement('dt');
      dt.textContent = term;
      const dd = document.createElement('dd');
      dd.textContent = value;
      meta.append(dt, dd);
    }
    if (meta.childElementCount) panel.append(meta);

    if (summary.fullTrackingUrl && labels.view_full_tracking) {
      const link = document.createElement('a');
      link.className = 'contact-drawer__tracking-link button';
      link.href = summary.fullTrackingUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.textContent = labels.view_full_tracking;
      panel.append(link);
    }

    if (labels.track_another) {
      // type=button: the panel is a sibling of the form, but be explicit so
      // this can never be mistaken for a submit if the markup ever moves.
      const back = document.createElement('button');
      back.type = 'button';
      back.className = 'contact-drawer__tracking-back';
      back.textContent = labels.track_another;
      back.addEventListener('click', () => this.#restoreTrackingForm());
      panel.append(back);
    }

    // Same approach as #showSuccess: hide rather than remove, so the Component
    // base's requiredRefs stay satisfied and its observer does not throw.
    form.hidden = true;
    form.after(panel);
    panel.focus();
  }

  /**
   * Returns the drawer to the lookup form after a result was shown, cleared
   * for a fresh lookup.
   */
  #restoreTrackingForm() {
    const { form, formError } = this.refs;

    this.querySelector('.contact-drawer__tracking')?.remove();
    form.hidden = false;

    if (formError) formError.hidden = true;

    for (const name of ['orderNumber', 'email']) {
      const field = form.elements.namedItem(name);
      if (field instanceof HTMLInputElement) {
        field.value = '';
        this.#setFieldError(field, false);
      }
    }
    const orderNumber = form.elements.namedItem('orderNumber');
    if (orderNumber instanceof HTMLElement) orderNumber.focus();
  }

  /**
   * @param {string} iso - ISO-8601 timestamp, or empty.
   * @returns {string} Date in the page's locale, or '' when unparseable.
   */
  #formatDate(iso) {
    if (!iso) return '';
    // AfterShip also emits 'YYYY-MM-DD HH:mm:ss' (space, no zone), which
    // Safari rejects as Invalid Date — normalise it to the 'T' form first.
    const date = new Date(iso.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T'));
    if (Number.isNaN(date.getTime())) return '';
    const locale = document.documentElement.lang || undefined;
    return new Intl.DateTimeFormat(locale, { dateStyle: 'long' }).format(date);
  }

  /**
   * @param {string} name - Field to flag invalid and focus.
   */
  #markInvalid(name) {
    const field = this.refs.form.elements.namedItem(name);
    if (!(field instanceof HTMLElement)) return;

    this.#setFieldError(field, true);
    this.#showFormError(this.#validationMessage);
    field.focus();
  }

  /**
   * Marks or clears a field's error state: aria-invalid (the theme's visual
   * hook) plus aria-describedby pointing at the form-level error line, so a
   * screen reader on the focused field hears what is wrong — not just that
   * something is. aria-describedby is only removed when it points at our
   * error line, in case a field ever carries its own description.
   *
   * @param {HTMLElement} field
   * @param {boolean} invalid
   */
  #setFieldError(field, invalid) {
    const errorId = this.refs.formError?.id;

    if (invalid) {
      field.setAttribute('aria-invalid', 'true');
      if (errorId) field.setAttribute('aria-describedby', errorId);
    } else {
      field.removeAttribute('aria-invalid');
      if (errorId && field.getAttribute('aria-describedby') === errorId) {
        field.removeAttribute('aria-describedby');
      }
    }
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { submitButton } = this.refs;

    if (busy) {
      submitButton.dataset.label = submitButton.textContent.trim();
      submitButton.textContent = submitButton.dataset.busyLabel ?? submitButton.textContent;
    } else if (submitButton.dataset.label) {
      submitButton.textContent = submitButton.dataset.label;
    }
    submitButton.disabled = busy;
  }
}

if (!customElements.get('contact-form-component')) {
  customElements.define('contact-form-component', ContactFormComponent);
}
